// «Домены» of a site (stage I2): add a domain, see what to write in DNS, watch it go DNS → certificate → works; retry, move to another
// site of the account (the certificate stays) and remove.

import { useCallback, useEffect, useState } from 'react';
import type { Id } from '@confdeck/core-shared';
import { recordTitle, type RecordObj } from '@confdeck/core-shared';
import { Button, Input, Menu, MenuContent, MenuItem, MenuTrigger, useToast } from '../ui/primitives.tsx';
import { useMeta, useQuery, useRecord, useSync } from '../sync/react.tsx';
import s from './sites.module.css';

type Status = 'pending_dns' | 'verified' | 'issuing' | 'active' | 'error';
interface Domain { host: string; status: Status; attempts: number; lastError: string | null; checkedAt: string | null; dns: { cname: string | null; txtName: string; txtValue: string } }
interface State { domains: Domain[]; target: string | null; proxy: string }

const LABEL: Record<Status, string> = { pending_dns: 'Ждёт DNS', verified: 'DNS проверен', issuing: 'Выпускается сертификат', active: 'Работает', error: 'Ошибка' };

async function call(method: string, url: string, body?: unknown): Promise<State | { error: { message: string } }> {
  const r = await fetch(url, { method, credentials: 'same-origin', ...(body ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : {}) });
  return r.json().catch(() => ({ error: { message: 'Сервер не ответил' } }));
}

export function DomainsTab({ recordId }: { recordId: Id }) {
  const toast = useToast();
  const [state, setState] = useState<State | null>(null);
  const [host, setHost] = useState('');
  const [busy, setBusy] = useState(false);
  // The other live sites of the account: where a domain can be moved.
  const meta = useMeta();
  const client = useSync();
  const typeId = useRecord(recordId)?.typeId ?? '';
  const sites = useQuery(typeId, null, [], '').ids.filter((x) => x !== recordId).map((x) => client.store.get(x) as RecordObj | undefined).filter((r): r is RecordObj => !!r && !r.archivedAt);
  const base = `/api/sites/${recordId}/domains`;
  const apply = useCallback((r: State | { error: { message: string } }) => { if ('error' in r) toast({ kind: 'error', text: r.error.message }); else setState(r); }, [toast]);
  useEffect(() => { void call('GET', base).then(apply); }, [base, apply]);
  // A domain on its way is checked by the server in the background; the tab looks again every 20 seconds while one is.
  useEffect(() => {
    if (!state?.domains.some((d) => d.status !== 'active' && d.status !== 'error')) return;
    const t = setInterval(() => void call('GET', base).then(apply), 20_000);
    return () => clearInterval(t);
  }, [state, base, apply]);
  const act = async (method: string, url: string, body?: unknown) => { setBusy(true); apply(await call(method, url, body)); setBusy(false); };
  if (!state) return <p className={s.empty}>Загрузка…</p>;
  return (
    <div className={s.editor}>
      <p className={s.help}>Сайт открывается по своему домену после трёх шагов: вы настраиваете DNS, система проверяет его, получает сертификат Let’s Encrypt — и домен начинает работать.</p>
      <form className={s.bar} onSubmit={(e) => { e.preventDefault(); if (host.trim()) void act('POST', base, { host }).then(() => setHost('')); }}>
        <Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="conf.example.ru" aria-label="Домен" disabled={busy} />
        <Button type="submit" variant="primary" disabled={busy || !host.trim()}>Добавить домен</Button>
      </form>
      {!state.domains.length && <p className={s.empty}>Своих доменов нет. Основной сайт аккаунта открывается по адресу платформы, остальные — только по своему домену.</p>}
      <ul className={s.list}>
        {state.domains.map((d) => (
          <li key={d.host} className={s.item}>
            <div className={s.head}>
              <span className={s.name}>{d.host}<span className={s.sub}>{LABEL[d.status]}{d.status === 'pending_dns' && d.attempts ? ` · проверок: ${d.attempts}` : ''}</span></span>
              {d.status === 'active' && <a className={s.open} href={`https://${d.host}`} target="_blank" rel="noreferrer">Открыть</a>}
              {d.status !== 'active' && <Button size="sm" disabled={busy} onClick={() => void act('POST', `${base}/${encodeURIComponent(d.host)}/check`)}>Проверить снова</Button>}
              {sites.length > 0 && (
                <Menu>
                  <MenuTrigger asChild><Button size="sm" variant="ghost" disabled={busy}>Перенести…</Button></MenuTrigger>
                  <MenuContent align="end">
                    {sites.map((x) => (
                      <MenuItem key={x.id} onSelect={() => { if (confirm(`Перенести ${d.host} на «${recordTitle(x, meta)}»? Сертификат сохранится, по домену откроется этот сайт.`)) void act('POST', `${base}/${encodeURIComponent(d.host)}/move`, { to: x.id }); }}>{recordTitle(x, meta)}</MenuItem>
                    ))}
                  </MenuContent>
                </Menu>
              )}
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { if (confirm(`Убрать домен ${d.host}? Сайт перестанет по нему открываться.`)) void act('DELETE', `${base}/${encodeURIComponent(d.host)}`); }}>Убрать</Button>
            </div>
            {d.status !== 'active' && (
              <div className={s.params}>
                {d.lastError && <p className={s.error}>{d.lastError}</p>}
                <p className={s.help}>1. Подтвердите, что домен ваш: запись <b>TXT</b> {d.dns.txtName} со значением <code>{d.dns.txtValue}</code>.</p>
                {d.dns.cname && <p className={s.help}>2. Направьте домен на платформу: запись <b>CNAME</b> {d.host} → <b>{d.dns.cname}</b> (или A-записи с теми же адресами).</p>}
                <p className={s.help}>DNS проверяется каждые 5 минут в течение суток, сертификат — каждую минуту в течение часа.</p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
